/* eslint-disable max-lines */
import { $generateHtmlFromNodes } from '@lexical/html';
import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { editLanguageMatcher, setPromptCompareMode } from 'actions/identifier';
import { fetchPromptTemplates } from 'actions/promptTemplates';
import SelectRedesign from 'components/Select/SelectRedesign';
import ToggleButton from 'components/ToggleButton';
import Tooltip from 'components/Tooltip';
import WarningMsg from 'components/WarningMsg';
import { PLUS_ICON } from 'constants/filterIcons';
import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  getActiveLanguageMatcher,
  getActiveLanguageMatcherId,
  getCompareMode,
  getIdentifierCustomers,
  getLanguageMatcherTypes,
  getOriginalActiveLanguageMatcher,
  getSecondaryMatcher,
  getSelectedIdentifierRevision,
  getShouldDisableEdit,
  saveIdentifierRequestLoading,
  unsavedChanges,
} from 'selectors/identifier';
import { getPromptTemplates } from 'selectors/promptTemplates';
import { useSelector } from 'store';
import type { MLanguageMatcher } from 'types';
import LoadHtmlPlugin from 'utils/lexical/LoadHtmlPlugin';
import PlaceholderPlugin from 'utils/lexical/PlaceholderPlugin';
import { VariableNode } from 'utils/lexical/Variables/VariableNode';
import VariablesPlugin from 'utils/lexical/Variables/VariablesPlugin';
import { languageMatcherNotificationMsgs } from 'utils/userNotificationMsgs';

type LanguageMatcherType = {
  promptCompare: boolean;
  secondaryPrompt?: boolean;
};

const PromptBlockForm: React.FC<LanguageMatcherType> = ({
  promptCompare,
  secondaryPrompt = false,
}) => {
  const dispatch = useDispatch();

  const unsaved = useSelector(unsavedChanges);

  const identifierRevision = useSelector(getSelectedIdentifierRevision);
  const activeLanguageMatcherId = useSelector(getActiveLanguageMatcherId);
  const activeLanguageMatcher = useSelector(getActiveLanguageMatcher);
  const originalActiveLanguageMatcher = useSelector(getOriginalActiveLanguageMatcher);
  const customers = useSelector(getIdentifierCustomers);

  const isLoadingSaveIdentifier = useSelector(saveIdentifierRequestLoading);
  const languageMatcherTypes = useSelector(getLanguageMatcherTypes);
  const compareMode = useSelector(getCompareMode);
  const shouldDisable = useSelector(getShouldDisableEdit);

  const secondaryMatcher = useSelector(getSecondaryMatcher);
  const promptTemplates = useSelector(getPromptTemplates);

  const disabled = compareMode || shouldDisable || secondaryPrompt;

  useEffect(() => {
    dispatch(fetchPromptTemplates());
  }, [dispatch]);

  const resolvePlaceholder = (matcher?: MLanguageMatcher | null): string => {
    if (!matcher) return '';
    if (matcher.template_uuid) {
      const template = promptTemplates.find((t) => t.uuid === matcher.template_uuid);
      if (template?.pattern) return template.pattern;
    }
    return matcher.placeholder || '';
  };

  const activePlaceholder = useMemo(
    () => resolvePlaceholder(activeLanguageMatcher),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeLanguageMatcher, promptTemplates]
  );
  const secondaryPlaceholder = useMemo(
    () => resolvePlaceholder(secondaryMatcher),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [secondaryMatcher, promptTemplates]
  );

  useEffect(() => {
    if (activeLanguageMatcher && activeLanguageMatcher.isNew) {
      const input = document.getElementById('name');
      input?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLanguageMatcherId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!identifierRevision?.options_config) {
    return null;
  }

  const { variables = [] } = identifierRevision?.options_config as {
    variables: string[];
  };

  const handleChangeCustomer = (value: string): void => {
    dispatch(
      editLanguageMatcher({
        languageMatcherId: activeLanguageMatcher.uuid,
        // @ts-ignore
        data: { customer_uuids: [value] },
      })
    );
  };

  const onEditLanguageMatcher = (name: string, value: string | boolean): void => {
    if (!activeLanguageMatcher.uuid || value === null) return;
    dispatch(
      editLanguageMatcher({
        languageMatcherId: activeLanguageMatcher.uuid,
        data: { [name]: value },
      })
    );
  };

  const realizePlaceholder = (): void => {
    if (activePlaceholder) {
      dispatch(
        editLanguageMatcher({
          languageMatcherId: activeLanguageMatcher.uuid,
          data: { text: activePlaceholder },
        })
      );
    }
  };

  const handleExitCompareMode = (): void => {
    dispatch(setPromptCompareMode({ isComparing: false, secondaryMatcher: '' }));
  };

  const getMatcherText = (): string => {
    if (secondaryPrompt) {
      return secondaryMatcher.text || '';
    }
    return activeLanguageMatcher.text || '';
  };

  const initialConfig: InitialConfigType = {
    namespace: `block-${secondaryPrompt ? secondaryMatcher?.uuid : activeLanguageMatcherId}-editor`,
    nodes: [VariableNode],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any): void => {
      throw error;
    },
    editable: !disabled,
  };

  const getPromptInput = (): JSX.Element => {
    let placeholder = '';
    let className = '';

    if (secondaryPrompt) {
      placeholder = secondaryPlaceholder || '';
    } else {
      placeholder = activePlaceholder || '';
    }

    if (disabled) {
      className = 'bg-litlingo-gray-0.5';
    }

    if (activeLanguageMatcher.text !== originalActiveLanguageMatcher?.text && compareMode) {
      className = 'bg-litlingo-focus-area-background';
    }

    return (
      <div
        className={`w-full h-full flex relative overflow-hidden ${
          activeLanguageMatcher.enabled !== undefined && !activeLanguageMatcher.enabled
            ? 'text-litlingo-gray-4'
            : ''
        }`}
      >
        <LexicalComposer initialConfig={initialConfig}>
          <LoadHtmlPlugin content={getMatcherText()} />
          <VariablesPlugin
            initialOptions={variables.map((v) => ({
              value: v,
            }))}
          />
          <PlaceholderPlugin placeholder={placeholder} />

          <RichTextPlugin
            contentEditable={
              <ContentEditable
                id="text"
                name="text"
                className={`form-input w-full h-full p-2 rounded-md resize-none border-litlingo-gray-2 overflow-auto ${className}`}
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <OnChangePlugin
            onChange={(_editorState, editor): void => {
              editor.update(() => {
                const text = secondaryPrompt ? secondaryMatcher.text : activeLanguageMatcher.text;
                const htmlString = $generateHtmlFromNodes(editor);

                if (text !== htmlString) {
                  onEditLanguageMatcher('text', htmlString);
                }
              });
            }}
          />
        </LexicalComposer>
      </div>
    );
  };

  if (Object.keys(languageMatcherTypes).length === 0) return null;

  return (
    <div
      className={`h-full w-full max-w-150 ${
        promptCompare && !secondaryPrompt ? 'pt-8' : ''
      } flex flex-col gap-6 text-body overflow-hidden`}
    >
      <div className="w-full flex flex-row gap-2 items-center justify-between">
        <div className="flex flex-row gap-2.5 items-center">
          {!promptCompare && (
            <>
              <ToggleButton
                isOn={
                  activeLanguageMatcher.enabled !== undefined ? activeLanguageMatcher.enabled : true
                }
                handleToggleClick={(): void =>
                  onEditLanguageMatcher(
                    'enabled',
                    activeLanguageMatcher.enabled !== undefined
                      ? !activeLanguageMatcher.enabled
                      : false
                  )
                }
              />
              <p>Include block</p>
            </>
          )}
        </div>
        {!promptCompare ||
          (secondaryPrompt && (
            <div className="flex flex-row gap-2 items-center">
              <WarningMsg
                message={languageMatcherNotificationMsgs.unsavedChanges}
                showLoading={isLoadingSaveIdentifier}
                showWarning={unsaved}
                showSavedMsg={unsaved}
              />
              {secondaryPrompt && (
                <button
                  type="button"
                  className="w-33 h-8 button button--secondary justify-center"
                  onClick={(): void => {
                    handleExitCompareMode();
                  }}
                >
                  <span className="font-bold">Exit Compare</span>
                </button>
              )}
            </div>
          ))}
      </div>
      {promptCompare && secondaryPrompt && !secondaryMatcher ? (
        <div className="flex w-full mt-6">
          <p className="italic mx-auto">
            Select a second prompt block to compare the pattern side by side.
          </p>
        </div>
      ) : (
        <div className="h-full w-full flex flex-col gap-4">
          <div className="w-full flex flex-row gap-2">
            <label htmlFor="name" className="max-w-104 flex flex-col gap-1 text-body flex-1">
              <div className="flex flex-row">
                <span>Block Name</span>
                <span className="text-litlingo-alert">*</span>
              </div>
              <input
                name="name"
                id="name"
                data-testid="matcher-name-input"
                value={secondaryPrompt ? secondaryMatcher.name : activeLanguageMatcher.name}
                className={`form-input w-full py-2 px-4 border border-litlingo-gray-2 rounded-lg text-body focus:outline-none ${
                  activeLanguageMatcher.name !== originalActiveLanguageMatcher?.name && compareMode
                    ? 'bg-litlingo-focus-area-background'
                    : ''
                }`}
                onChange={(e): void => {
                  onEditLanguageMatcher(e.target.name, e.target.value);
                }}
                disabled={
                  compareMode ||
                  shouldDisable ||
                  secondaryPrompt ||
                  (activeLanguageMatcher.enabled !== undefined && !activeLanguageMatcher.enabled)
                }
              />
            </label>
            {!promptCompare && activeLanguageMatcher.parent && (
              <div className="w-45 flex flex-col gap-1 text-body">
                <div className="flex flex-row">
                  <span>Workspaces</span>
                  <span className="text-litlingo-alert">*</span>
                </div>
                <SelectRedesign
                  onChange={handleChangeCustomer}
                  options={customers.map((c) => ({ label: c.name || '', value: c.uuid }))}
                  // @ts-ignore
                  value={activeLanguageMatcher.customer_uuids?.[0]}
                  className="h-9"
                  disabled={
                    activeLanguageMatcher.enabled !== undefined && !activeLanguageMatcher.enabled
                  }
                />
              </div>
            )}
          </div>
          <div className="h-full w-full flex flex-row items-start overflow-hidden">
            <label htmlFor="text" className="h-full w-full flex flex-col gap-1 text-body">
              <div className="flex flex-row">
                <span>Pattern</span>
                <span className="text-litlingo-alert">*</span>
              </div>
              <div className="h-full w-full flex flex-row items-start gap-1 text-body overflow-hidden">
                {getPromptInput()}

                {!(compareMode || shouldDisable || promptCompare || activeLanguageMatcher.text) && (
                  <Tooltip content="Add placeholder text">
                    <button
                      type="button"
                      className="w-5 h-5 p-0.5 flex items-center justify-center rounded border border-litlingo-gray-2 focus:outline-none"
                      onClick={realizePlaceholder}
                    >
                      {PLUS_ICON()}
                    </button>
                  </Tooltip>
                )}
              </div>
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptBlockForm;
